import { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    LinearProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import FileUploadArea, { type ExistingDocument, type StagedFile } from 'modules/common/components/FileUploadArea';
import ConfirmationDialog from 'modules/common/components/MUI/ConfirmationDialog';
import { Document } from 'modules/common/interfaces/documents';

export interface UploadedFile {
    id?: string;
    uuid?: string;
    file_name: string;
    file_size?: number;
}

export interface UploadDocumentModalProps {
    /** Whether the modal is open */
    readonly open: boolean;
    /** Title shown in modal header (e.g., "drivers license") */
    readonly title: string;
    /** Document type for categorization */
    readonly documentType: string;
    /** Comma-separated list of accepted file extensions (e.g. ".pdf,.jpg"). Defaults to the standard document set. */
    readonly accept?: string;
    /** Called when modal is closed */
    readonly onClose: () => void;
    /** Called when Save is clicked with uploaded files */
    readonly onSave: (uploadedFiles: UploadedFile[]) => void;
    /** Called when Cancel is clicked */
    readonly onCancel: () => void;
    /** Optional custom upload endpoint */
    readonly uploadEndpoint?: string;
    /** Custom metadata to attach to uploads */
    readonly meta?: Record<string, unknown>;
    /** When set, shows a required year selector and tags the upload with the chosen year (`meta.year`). */
    readonly yearOptions?: number[];
    /** Initial selected year when `yearOptions` is provided (defaults to the first option). */
    readonly defaultYear?: number;
    /** Existing documents already uploaded (from backend) */
    readonly existingDocuments?: ExistingDocument[];
    /** Called when an existing document is deleted */
    readonly onDeleteExistingDocument?: (docId: string) => void;
    /** Called when upload fails */
    readonly onUploadError?: (error: Error) => void;
}

export default function UploadDocumentModal({
    open,
    title,
    documentType,
    accept = '.pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png',
    onClose,
    onSave,
    onCancel,
    uploadEndpoint = undefined,
    meta = {},
    yearOptions,
    defaultYear,
    existingDocuments = [],
    onDeleteExistingDocument,
    onUploadError,
}: UploadDocumentModalProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [uploadTrigger, setUploadTrigger] = useState(0);
    const [selectedYear, setSelectedYear] = useState<number | undefined>(defaultYear ?? yearOptions?.[0]);

    const showYearSelector = !!yearOptions?.length;
    // The chosen year takes precedence so the upload is tagged with the document's actual year.
    const effectiveMeta = useMemo(
        () => (showYearSelector ? { ...meta, year: selectedYear } : meta),
        [showYearSelector, meta, selectedYear]
    );

    // Use a unique key that changes when modal opens to reset FileUploadArea state
    const fileUploadKey = useMemo(() => `upload-${open ? Date.now() : 'closed'}`, [open]);

    // Reset state when modal closes (not when it opens)
    useEffect(() => {
        if (!open) {
            setUploadedFiles([]);
            setIsUploading(false);
            setStagedFiles([]);
            setShowCancelConfirm(false);
            setUploadTrigger(0);
            setSelectedYear(defaultYear ?? yearOptions?.[0]);
        }
    }, [open, defaultYear, yearOptions]);

    const handleYearChange = useCallback((event: SelectChangeEvent<number>) => {
        setSelectedYear(Number(event.target.value));
    }, []);

    const handleSave = useCallback(() => {
        if (stagedFiles.length > 0) {
            // Trigger upload - completion will be handled by handleUploadComplete
            setIsUploading(true);
            setUploadTrigger((prev) => prev + 1);
        } else {
            // No staged files, just save any already uploaded files and close
            if (uploadedFiles.length > 0) {
                onSave(uploadedFiles);
            }
            onClose();
        }
    }, [stagedFiles, uploadedFiles, onSave, onClose]);

    const handleUploadComplete = useCallback(
        (docs: Document[]) => {
            setIsUploading(false);
            const allFiles = [...uploadedFiles, ...docs] as UploadedFile[];
            if (allFiles.length > 0) {
                onSave(allFiles);
            }
            onClose();
        },
        [uploadedFiles, onSave, onClose]
    );

    const handleCancelClick = useCallback(() => {
        if (stagedFiles.length > 0) {
            setShowCancelConfirm(true);
        } else {
            onCancel();
        }
    }, [stagedFiles, onCancel]);

    const handleConfirmCancel = useCallback(() => {
        // Just close - the key change on next open will reset FileUploadArea
        setShowCancelConfirm(false);
        onCancel();
    }, [onCancel]);

    const handleCloseClick = useCallback(() => {
        if (stagedFiles.length > 0) {
            setShowCancelConfirm(true);
        } else {
            onClose();
        }
    }, [stagedFiles, onClose]);

    const handleUploadSuccess = useCallback((files: Document[]) => {
        // FileUploadArea passes accumulated files array (for non-deferred uploads)
        setUploadedFiles(files as UploadedFile[]);
        setIsUploading(false);
    }, []);

    const handleUploadingChange = useCallback((uploading: boolean) => {
        setIsUploading(uploading);
    }, []);

    const handleStagedFilesChange = useCallback((files: StagedFile[]) => {
        setStagedFiles(files);
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleCloseClick}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: 3,
                        overflow: 'hidden',
                    },
                }}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 3,
                        px: 3,
                        borderBottom: 1,
                        borderColor: 'divider',
                        m: 0,
                    }}>
                    <Typography variant="h3" color="text.primary">
                        Upload {title}
                    </Typography>
                    <IconButton
                        onClick={handleCloseClick}
                        size="small"
                        sx={{
                            bgcolor: 'cement.hover',
                            color: 'text.primary',
                            '&:hover': { bgcolor: 'cement.hover' },
                        }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </DialogTitle>

                {isUploading && <LinearProgress color="primary" sx={{ height: 4 }} />}

                <DialogContent sx={{ p: 3 }}>
                    <Stack spacing={3}>
                        {showYearSelector && (
                            <FormControl variant="outlined" size="select" fullWidth>
                                <InputLabel id="upload-document-year-label">Tax year</InputLabel>
                                <Select
                                    labelId="upload-document-year-label"
                                    label="Tax year"
                                    value={selectedYear ?? ''}
                                    onChange={handleYearChange}>
                                    {yearOptions?.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FileUploadArea
                            key={fileUploadKey}
                            documentType={documentType}
                            meta={effectiveMeta}
                            uploadEndpoint={uploadEndpoint}
                            onUploadSuccess={handleUploadSuccess}
                            onUploadError={onUploadError}
                            onUploadingChange={handleUploadingChange}
                            accept={accept}
                            existingDocuments={existingDocuments}
                            onDeleteExistingDocument={onDeleteExistingDocument}
                            deferUpload
                            uploadTrigger={uploadTrigger}
                            onUploadComplete={handleUploadComplete}
                            onStagedFilesChange={handleStagedFilesChange}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', gap: 1, m: 0 }}>
                    <Button variant="outlined" color="primary" onClick={handleCancelClick} sx={{ minWidth: 100 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={isUploading || (showYearSelector && !selectedYear)}
                        sx={{ minWidth: 100 }}>
                        {isUploading ? 'Uploading...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Confirmation Dialog */}
            <ConfirmationDialog
                open={showCancelConfirm}
                handleClose={() => setShowCancelConfirm(false)}
                title="Discard changes?"
                message="You have files that haven't been saved. Are you sure you want to discard them?"
                reject={{
                    label: 'Keep editing',
                    onClick: () => setShowCancelConfirm(false),
                    variant: 'outlined',
                }}
                accept={{
                    label: 'Discard',
                    onClick: handleConfirmCancel,
                    variant: 'contained',
                }}
            />
        </>
    );
}
